import { useState } from "react";
import PasswordGate from "../components/PasswordGate";
import { formatRelativeTime } from "../utils/formatRelativeTime";

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark p-5">
      <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">{label}</div>
      <div className="text-3xl font-bold text-slate-900 dark:text-white">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function TrafficSources({ sources }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark p-5">
      <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Traffic Sources</h2>
      {sources.length > 0 ? (
        <div className="space-y-3">
          {sources.map((item) => (
            <div key={item.source} className="flex items-center justify-between text-sm">
              <span className="text-slate-700 dark:text-slate-300 truncate mr-3">
                {item.source}
              </span>
              <span className="text-slate-500 dark:text-slate-400 tabular-nums shrink-0">
                {item.visits.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-400 text-center py-6 text-sm">No traffic data yet</p>
      )}
    </div>
  );
}

function RecentDownloads({ downloads }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark p-5">
      <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Recent Downloads</h2>
      {downloads.length > 0 ? (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <th className="pb-2 pr-4 font-medium">When</th>
                <th className="pb-2 pr-4 font-medium">Location</th>
                <th className="pb-2 font-medium">Browser</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {downloads.map((dl, i) => (
                <tr key={i}>
                  <td className="py-2.5 pr-4 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {formatRelativeTime(dl.timestamp)}
                  </td>
                  <td className="py-2.5 pr-4 text-slate-600 dark:text-slate-400">
                    {dl.city && dl.city !== "Unknown"
                      ? `${dl.city}, ${dl.country}`
                      : dl.country}
                  </td>
                  <td className="py-2.5 text-slate-600 dark:text-slate-400">
                    {dl.browser}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-slate-400 text-center py-6 text-sm">No downloads yet</p>
      )}
    </div>
  );
}

export default function Stats() {
  const [password, setPassword] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadStats = async (pw) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/get-stats.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });

      if (response.status === 401) {
        setPassword(null);
        setError("Session expired. Please re-enter password.");
        return;
      }

      if (!response.ok) throw new Error("Failed to load stats");

      setStats(await response.json());
    } catch {
      setError("Failed to load statistics.");
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = (pw) => {
    setPassword(pw);
    loadStats(pw);
  };

  if (!password) {
    return <PasswordGate onAuthenticate={handleAuthenticate} />;
  }

  if (loading && !stats) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-slate-500 dark:text-slate-400">Loading statistics...</div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => loadStats(password)}
            className="px-4 py-2 bg-primary-400 hover:bg-primary-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Dashboard</h1>
          {stats.generatedAt && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Last updated {formatRelativeTime(stats.generatedAt)}
            </p>
          )}
        </div>
        <button
          onClick={() => loadStats(password)}
          disabled={loading}
          className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-sm transition-colors text-slate-700 dark:text-slate-300 disabled:opacity-50"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Visitors Today" value={stats.visitors.today} />
        <StatCard label="Total Visitors" value={stats.visitors.total} />
        <StatCard label="Downloads Today" value={stats.downloads.today} />
        <StatCard label="Total Downloads" value={stats.downloads.total} />
      </div>

      {/* Traffic Sources + Recent Downloads */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TrafficSources sources={stats.trafficSources} />
        <RecentDownloads downloads={stats.recentDownloads} />
      </div>
    </div>
  );
}
